import { Signal, signal, Type } from '@angular/core';
import { UserType } from '@logaia_users/logaia-dto';
import { estErreurChargementEcran, rechargerApresEchecChargement } from './chargement-ecran';

/**
 * Règle centrale des fonctionnalités activées selon la configuration de build.
 *
 * Accès collaborateur : un seul réglage, `define.LOGAIA_COLLAB_BUILD` dans angular.json (true en develop et local,
 * false par défaut, donc en production et préproduction : version « client seul »). C'est une valeur littérale dans
 * le code compilé : le compilateur écarte du build tout code placé derrière elle quand elle vaut false.
 * Changer la valeur demande de relancer ng serve.
 *
 * Quand il est inactif : routes collaborateur non déclarées (écrans absents du build), connexion d'un collaborateur
 * refusée, session collaborateur existante refusée, mode « en tant que » (asUser) ignoré,
 * et `CollaborateurService.isCollab` toujours faux (en-tête, menus, accueil et aide en version client).
 *
 * Code collaborateur dans un fichier commun au client (build « client seul » sans aucune trace collaborateur) :
 * - données ou traitement : derrière `LOGAIA_COLLAB_BUILD` écrit tel quel dans ce fichier (la constante n'est remplacée
 *   que là où elle est écrite : une constante importée qui la recopie ne suffit pas à écarter le code), ex.
 *   `...(LOGAIA_COLLAB_BUILD ? [texteCollab] : [])` ou `if (LOGAIA_COLLAB_BUILD && collab) { … }` ;
 * - composant collaborateur affiché dans un écran commun : chargé à la demande avec `composantCollab` (fichier à part,
 *   jamais importé statiquement par le code client).
 */

/** Message affiché à un collaborateur qui se connecte quand l'accès collaborateur est inactif. */
export const MSG_COLLAB_INDISPONIBLE = "L’accès collaborateur n’est pas encore disponible sur cette version.";

/**
 * Profil ouvert sur cette version : tout profil si l'accès collaborateur est actif, sinon le client seulement.
 * Correctif (revue avant la mise en production client) : un profil Admin ou Assistance passait la garde de connexion
 * alors que la connexion les refuse, et échappait au contrôle des droits client.
 */
export function profilOuvert(userType: UserType | undefined): boolean {
  return LOGAIA_COLLAB_BUILD || userType === UserType.Client;
}

/**
 * Composant collaborateur chargé à la demande (à afficher avec `*ngComponentOutlet`) : null tant qu'il n'est pas chargé,
 * et toujours null sans chargeur. À appeler avec la constante écrite dans le fichier appelant, pour que le compilateur
 * écarte le chargeur (et le fichier du composant) du build « client seul » :
 * `composantCollab(LOGAIA_COLLAB_BUILD && estCollab && (() => avecReprise(() => import('./x.component')).then((m) => m.XComponent)))`
 * (reprise automatique du chargement : core/chargement-ecran.ts). La sélection de l'export reste dans le chargeur, écarté
 * avec lui du build « client seul ».
 */
export function composantCollab<T>(charger: false | (() => Promise<Type<T>>)): Signal<Type<T> | null> {
  const composant = signal<Type<T> | null>(null);
  if (charger) {
    charger().then(
      (c) => composant.set(c),
      // Fichier du composant toujours introuvable après la reprise (fichier dépendant en échec, onglet ouvert avant une
      // nouvelle version) : sinon menu vide ou accueil en chargement sans fin ; la page est rechargée une fois.
      (err: unknown) => {
        if (estErreurChargementEcran(err)) {
          rechargerApresEchecChargement();
        }
      },
    );
  }
  return composant.asReadonly();
}
